/** Do not show message actions when tapping controls or profile triggers. */
export const messageGestureIgnore = 'button, a, input, textarea, select, img, video, audio, iframe, [contenteditable], [role="button"], [role="slider"], [role="toolbar"], [role="menu"], [role="dialog"], [data-message-gesture-ignore]'

// Tappable content can also start a reply swipe: its click is consumed only
// after the horizontal gesture locks. Native players and drag controls keep
// their own gestures. Our waveform seeks on click, so it can opt in explicitly.
const messageSwipeIgnore = 'input, textarea, select, video, audio, iframe, [contenteditable], [role="slider"]:not([data-message-swipe-surface]), [role="scrollbar"], [role="listbox"], [role="toolbar"], [role="menu"], [role="dialog"], [data-message-gesture-ignore]'

/** Lock only a deliberate horizontal swipe; vertical scrolling, pinch zoom and controls stay native. */
export function bindMessageSwipe(row: HTMLElement, onReply: () => void): () => void {
  let gesture: { id: number; x: number; y: number; swipe: boolean; distance: number } | null = null
  let suppressClickUntil = 0
  let clickTimer: ReturnType<typeof setTimeout> | undefined
  const clearClickSuppression = () => {
    suppressClickUntil = 0
    clearTimeout(clickTimer)
    document.removeEventListener('click', click, true)
    document.removeEventListener('pointerdown', nextPress, true)
  }
  const nextPress = (e: PointerEvent) => { if (e.isPrimary) clearClickSuppression() }
  const suppressClick = (duration: number) => {
    clearClickSuppression()
    suppressClickUntil = Date.now() + duration
    // Releasing a swipe must not activate the message or a control under the finger.
    document.addEventListener('click', click, true)
    document.addEventListener('pointerdown', nextPress, true)
    clickTimer = setTimeout(clearClickSuppression, duration)
  }
  const selected = () => Boolean(window.getSelection()?.toString())
  const stop = () => {
    if (gesture?.swipe) {
      row.style.removeProperty('--reply-swipe')
      row.style.removeProperty('--reply-progress')
      delete row.dataset.swiping
      delete row.dataset.swipeReady
    }
    gesture = null
    document.removeEventListener('pointerdown', otherPointer, true)
    document.removeEventListener('pointermove', move, true)
    document.removeEventListener('pointerup', release, true)
    document.removeEventListener('pointercancel', cancel, true)
    document.removeEventListener('touchstart', otherTouch, true)
    document.removeEventListener('touchmove', touchMove, true)
    document.removeEventListener('touchend', touchEnd, true)
    document.removeEventListener('touchcancel', cancel, true)
    document.removeEventListener('scroll', scroll, true)
    document.removeEventListener('selectionchange', selectionChanged)
    document.removeEventListener('visibilitychange', visibilityChanged)
    window.removeEventListener('resize', cancel)
  }
  const cancel = () => { stop() }
  const scroll = () => { if (!gesture?.swipe) stop() }
  const selectionChanged = () => { if (selected()) stop() }
  const visibilityChanged = () => { if (document.hidden) stop() }
  const otherPointer = (e: PointerEvent) => { if (gesture && e.pointerId !== gesture.id) stop() }
  const movePoint = (x: number, y: number) => {
    if (!gesture) return
    const dx = gesture.x - x
    const dy = y - gesture.y
    if (Math.hypot(dx, dy) <= 10 && !gesture.swipe) return
    // Direction matters only before locking the swipe. Once claimed, the same
    // message owns this finger until release, including vertical drift and retargeting.
    if (!gesture.swipe && (dx <= 0 || dx < Math.abs(dy) * 1.4)) { stop(); return }
    gesture.swipe = true
    const distance = Math.max(0, dx)
    gesture.distance = distance
    row.style.setProperty('--reply-progress', String(Math.min(distance / 64, 1)))
    row.dataset.swiping = ''
    row.dataset.swipeReady = distance >= 64 ? 'true' : 'false'
    row.style.setProperty('--reply-swipe', `${-(Math.min(distance, 64) + Math.max(0, distance - 64) * 0.15)}px`)
  }
  const move = (e: PointerEvent) => {
    if (gesture?.id === e.pointerId) movePoint(e.clientX, e.clientY)
  }
  const finish = () => {
    if (!gesture) return
    const reply = gesture.swipe && gesture.distance >= 64
    if (gesture.swipe) suppressClick(750)
    stop()
    if (reply) onReply()
  }
  const release = (e: PointerEvent) => { if (gesture?.id === e.pointerId) finish() }
  const otherTouch = (e: TouchEvent) => { if (e.touches.length !== 1) stop() }
  const touchMove = (e: TouchEvent) => {
    if (!gesture) return
    if (e.touches.length !== 1) { stop(); return }
    const touch = e.touches[0]
    if (!touch) return
    if (touch.identifier !== gesture.id) return
    movePoint(touch.clientX, touch.clientY)
    // Safari can hand a pan-y pointer gesture to its scroll view. Claim only the
    // horizontal axis through a non-passive touch listener before that happens.
    if (gesture?.swipe && e.cancelable) e.preventDefault()
  }
  const touchEnd = (e: TouchEvent) => {
    if (gesture && Array.from(e.changedTouches).some(t => t.identifier === gesture?.id)) finish()
  }
  const begin = (id: number, x: number, y: number, touch: boolean, target: EventTarget | null) => {
    clearClickSuppression()
    if (selected() || x < 24 || x > window.innerWidth - 24 || !(target instanceof Element) || target.closest(messageSwipeIgnore)) return
    stop()
    gesture = { id, x, y, swipe: false, distance: 0 }
    if (touch) {
      document.addEventListener('touchstart', otherTouch, { capture: true, passive: true })
      document.addEventListener('touchmove', touchMove, { capture: true, passive: false })
      document.addEventListener('touchend', touchEnd, true)
      document.addEventListener('touchcancel', cancel, true)
    } else {
      document.addEventListener('pointerdown', otherPointer, { capture: true, passive: true })
      document.addEventListener('pointermove', move, { capture: true, passive: true })
      document.addEventListener('pointerup', release, true)
      document.addEventListener('pointercancel', cancel, true)
    }
    document.addEventListener('scroll', scroll, { capture: true, passive: true })
    document.addEventListener('selectionchange', selectionChanged)
    document.addEventListener('visibilitychange', visibilityChanged)
    window.addEventListener('resize', cancel)
  }
  const down = (e: PointerEvent) => {
    if ('ontouchstart' in window || e.defaultPrevented || e.pointerType !== 'touch' || !e.isPrimary || e.button !== 0) return
    begin(e.pointerId, e.clientX, e.clientY, false, e.target)
  }
  const touchStart = (e: TouchEvent) => {
    if (e.defaultPrevented || e.touches.length !== 1) return
    const touch = e.touches[0]
    if (!touch) return
    begin(touch.identifier, touch.clientX, touch.clientY, true, e.target)
  }
  const click = (e: MouseEvent) => {
    if (e.detail === 0 || Date.now() > suppressClickUntil) return
    clearClickSuppression()
    e.preventDefault()
    e.stopPropagation()
  }
  row.addEventListener('pointerdown', down)
  row.addEventListener('touchstart', touchStart, { passive: true })
  return () => {
    stop()
    clearClickSuppression()
    row.removeEventListener('pointerdown', down)
    row.removeEventListener('touchstart', touchStart)
  }
}
